import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { announcementApi, taskApi, workflowMarketApi } from '../services/api';
import { Announcement, SubTaskInfo, WorkflowMarket } from '../types';
import { message } from 'antd';
import { normalizeAnnouncementContent } from '../utils/announcementContent';
import {
  RocketOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  PlayCircleOutlined,
  ArrowRightOutlined,
  UserOutlined,
  LogoutOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import TrialLimitModal from '../components/TrialLimitModal';

const HomePageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTasks: 0,
    runningTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    successRate: 0
  });
  const [recentTasks, setRecentTasks] = useState<SubTaskInfo[]>([]);
  const [popularWorkflows, setPopularWorkflows] = useState<WorkflowMarket[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadAnnouncements();
  }, [user]);

  /**
   * Load active announcements for the dashboard page.
   */
  const loadAnnouncements = async () => {
    try {
      const response = await announcementApi.getAnnouncements();
      if (response.success && response.data) {
        setAnnouncements(response.data);
      }
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        setLoading(false);
        return;
      }
      
      // 加载任务统计数据
      const tasksResponse = await taskApi.getSubTasks(user.user_id);
      if (tasksResponse.success && tasksResponse.data && tasksResponse.data.tasks) {
        const tasks = tasksResponse.data.tasks;
        const totalTasks = tasks.length;
        const runningTasks = tasks.filter((t: SubTaskInfo) => t.status === 'processing').length;
        const completedTasks = tasks.filter((t: SubTaskInfo) => t.status === 'success').length;
        const failedTasks = tasks.filter((t: SubTaskInfo) => t.status === 'fail' || t.status === 'create_fail').length;
        const successRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        
        setStats({
          totalTasks,
          runningTasks,
          completedTasks,
          failedTasks,
          successRate
        });
        
        setRecentTasks(tasks.slice(0, 5));
      }
      
      // 加载热门工作流
      const workflowsResponse = await workflowMarketApi.getPopularWorkflows(user.user_id);
      if (workflowsResponse.success && workflowsResponse.data) {
        setPopularWorkflows(workflowsResponse.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('加载仪表板数据失败:', error);
      setLoading(false);
    }
  };

  /**
   * Format announcement publish time for dashboard display.
   */
  const formatAnnouncementDate = (publishAt: string) => {
    return new Date(publishAt).toLocaleString('zh-CN');
  };

  const handleWorkflowExecute = async (workflow: WorkflowMarket) => {
    const marketId = workflow.market_id || workflow.id;
    if (marketId) {
      navigate(`/ai-conversation/${marketId}`);
    } else {
      message.warning('该工作流配置错误，无法打开对话页面');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'processing':
        return 'text-blue-600 bg-blue-50';
      case 'fail':
      case 'create_fail':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined className="text-green-600" />;
      case 'processing':
        return <PlayCircleOutlined className="text-blue-600" />;
      case 'fail':
      case 'create_fail':
        return <CloseCircleOutlined className="text-red-600" />;
      default:
        return <ClockCircleOutlined className="text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return '已完成';
      case 'processing':
        return '运行中';
      case 'fail':
      case 'create_fail':
        return '失败';
      default:
        return '等待中';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-6">
      <div key="main-content" className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div key="welcome-section" className="mb-8">
            <h1 key="welcome-title" className="text-4xl font-bold text-gray-900 mb-2">
              欢迎回来，{user?.user_name}！
            </h1>
            <p key="welcome-subtitle" className="text-lg text-gray-600">
              这里是您的工作流管理中心
            </p>
          </div>

          {/* Stats Grid */}
          <div key="stats-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Tasks */}
            <div key="total-tasks" className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all">
              <div key="total-tasks-icon" className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                  <TrophyOutlined style={{ fontSize: '24px', color: '#4F46E5' }} />
                </div>
              </div>
              <div key="total-tasks-value" className="text-3xl font-bold text-gray-900 mb-1">{stats.totalTasks}</div>
              <div key="total-tasks-label" className="text-sm text-gray-600">总任务数</div>
            </div>

            {/* Running Tasks */}
            <div key="running-tasks" className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all">
              <div key="running-tasks-icon" className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center">
                  <PlayCircleOutlined style={{ fontSize: '24px', color: '#0EA5E9' }} />
                </div>
              </div>
              <div key="running-tasks-value" className="text-3xl font-bold text-gray-900 mb-1">{stats.runningTasks}</div>
              <div key="running-tasks-label" className="text-sm text-gray-600">运行中</div>
            </div>

            {/* Completed Tasks */}
            <div key="completed-tasks" className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all">
              <div key="completed-tasks-icon" className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircleOutlined style={{ fontSize: '24px', color: '#10B981' }} />
                </div>
              </div>
              <div key="completed-tasks-value" className="text-3xl font-bold text-gray-900 mb-1">{stats.completedTasks}</div>
              <div key="completed-tasks-label" className="text-sm text-gray-600">已完成</div>
            </div>

            {/* Success Rate */}
            <div key="success-rate" className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all">
              <div key="success-rate-icon" className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <TrophyOutlined style={{ fontSize: '24px', color: '#F59E0B' }} />
                </div>
              </div>
              <div key="success-rate-value" className="text-3xl font-bold text-gray-900 mb-1">{stats.successRate}%</div>
              <div key="success-rate-label" className="text-sm text-gray-600">成功率</div>
              <div key="success-rate-bar" className="mt-3 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${stats.successRate}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <NotificationOutlined style={{ fontSize: '24px', color: '#4F46E5' }} />
              <h2 className="text-xl font-bold text-gray-900">公告栏</h2>
            </div>
            <div
              className={
                announcements.length > 3
                  ? 'h-[252px] overflow-y-scroll space-y-4 pr-2'
                  : 'space-y-4'
              }
            >
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">暂无公告</div>
              ) : (
                announcements.map((announcement) => (
                  <button
                    key={announcement.announcement_id}
                    type="button"
                    onClick={() => setSelectedAnnouncement(announcement)}
                    className="w-full rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-200 hover:bg-indigo-50/40 transition-all"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <h3 className="text-base font-semibold text-gray-900">{announcement.title}</h3>
                      <span className="text-xs text-gray-500">
                        {formatAnnouncementDate(announcement.publish_at)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div key="content-grid" className="grid lg:grid-cols-2 gap-8">
            {/* Recent Tasks */}
            <div key="recent-tasks" className="bg-white rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div key="recent-tasks-header" className="flex items-center space-x-3">
                  <ClockCircleOutlined style={{ fontSize: '24px', color: '#4F46E5' }} />
                  <h2 className="text-xl font-bold text-gray-900">最近任务</h2>
                </div>
                <button
                  key="recent-tasks-button"
                  onClick={() => navigate('/task-queue')}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-all"
                >
                  查看全部
                </button>
              </div>

              <div className="space-y-4">
                {recentTasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    暂无任务记录
                  </div>
                ) : (
                  recentTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-all cursor-pointer"
                      onClick={() => navigate('/task-queue')}
                    >
                      <div key={`icon-${task.id}`} className="flex-shrink-0 mt-1">
                        {getStatusIcon(task.status)}
                      </div>
                      <div key={`content-${task.id}`} className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {task.task_name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          任务ID: {task.task_id}
                        </p>
                        <p className="text-xs text-gray-500">
                          创建时间: {new Date(task.created_at).toLocaleString('zh-CN')}
                        </p>
                      </div>
                      <div key={`status-${task.id}`}>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {getStatusText(task.status)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Popular Workflows */}
            <div key="popular-workflows" className="bg-white rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div key="workflows-header" className="flex items-center space-x-3">
                  <RocketOutlined style={{ fontSize: '24px', color: '#4F46E5' }} />
                  <h2 className="text-xl font-bold text-gray-900">热门工作流</h2>
                </div>
                <button
                  key="workflows-button"
                  onClick={() => navigate('/workflow-market')}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-all"
                >
                  浏览市场
                </button>
              </div>

              <div className="space-y-4">
                {popularWorkflows.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    暂无工作流
                  </div>
                ) : (
                  popularWorkflows.map((workflow) => (
                    <div
                      key={workflow.market_id}
                      className="group p-4 rounded-xl border border-gray-200 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => handleWorkflowExecute(workflow)}
                    >
                      <div className="flex items-start justify-between">
                        <div key={`workflow-content-${workflow.market_id}`} className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                            {workflow.display_name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {workflow.description || 'AI工作流自动化'}
                          </p>
                        </div>
                        <button key={`workflow-button-${workflow.market_id}`} className="ml-4 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all">
                          <ArrowRightOutlined style={{ fontSize: '16px' }} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
        </div>
      </div>

      {/* Trial Limit Modal */}
      {showTrialModal && (
        <TrialLimitModal 
          key="trial-modal"
          visible={showTrialModal}
          onClose={() => setShowTrialModal(false)}
          usedTimes={0}
          totalTimes={10}
        />
      )}

      {selectedAnnouncement && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          onClick={() => setSelectedAnnouncement(null)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
              <div className="pr-4">
                <h3 className="text-xl font-bold text-gray-900">{selectedAnnouncement.title}</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {formatAnnouncementDate(selectedAnnouncement.publish_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAnnouncement(null)}
                aria-label="Close announcement dialog"
                className="rounded-lg p-2 text-base text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all"
              >
                <CloseCircleOutlined />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              {selectedAnnouncement.summary ? (
                <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-600">
                  {selectedAnnouncement.summary}
                </div>
              ) : null}
              <div
                className="prose prose-sm mt-4 max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: normalizeAnnouncementContent(selectedAnnouncement.content) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePageDashboard;
